import {
  Accordion,
  AccordionDetails,
  Box,
  Breadcrumbs,
  Button,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Pagination from "./Pagination";
import SearchTextField from "../../../../components/SearchTextField";

interface ProcessDefinitions {
  id: string;
  name: string;
  key: string;
  version: number;
  startable: boolean;
  suspended: boolean;
  resource: string;
  deploymentId: string;
  tenantId: string;
  category: string;
  description: string;
  isExecutavelPortalExterno: boolean;
}

interface CategorieProcess {
  category: string;
  processDefinitions: ProcessDefinitions[];
}

interface CategorySelectionProps {
  categoriesProcess: CategorieProcess[];
  setCategory: (category: CategorieProcess | null) => void;
  navigateToHome?: () => void;
  tenantName?: string;
  isViewBreadcrumbs: boolean;
}

const CategorySelection: React.FC<CategorySelectionProps> = ({
  categoriesProcess,
  setCategory,
  navigateToHome,
  tenantName,
  isViewBreadcrumbs,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [dataCategories, setDataCategories] = useState<CategorieProcess[]>([]);
  const [page, setPage] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    setDataCategories(categoriesProcess);
  }, [categoriesProcess]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value.toLowerCase();
    const filtered = categoriesProcess.filter((request) =>
      request.category.toLowerCase().includes(searchTerm),
    );
    setDataCategories(filtered);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {isViewBreadcrumbs ? (
        <Box sx={{ padding: "0 16px", marginBottom: "16px" }}>
          <Breadcrumbs aria-label="breadcrumb" separator="›">
            <Typography
              color="inherit"
              key="0"
              onClick={() => navigateToHome?.()}
              sx={{ cursor: "pointer" }}
            >
              {t("common.catalog")}
            </Typography>
            {tenantName ? (
              <Typography
                color="text.primary"
                fontSize={20}
                fontWeight={500}
                key="1"
                onClick={() => navigateToHome?.()}
                sx={{ cursor: "pointer" }}
                variant="h6"
              >
                {tenantName}
              </Typography>
            ) : null}
            <Typography />
          </Breadcrumbs>
        </Box>
      ) : null}
      <SearchTextField
        handleSearch={handleSearch}
        placeholder={t("home.searchCategoryPlaceholder")}
        title={t("home.searchCategoryTitle")}
      />
      <Accordion className="accordion" defaultExpanded>
        {dataCategories
          .slice(page * itemsPerPage, (page + 1) * itemsPerPage)
          .map(({ processDefinitions, category }) => (
            <AccordionDetails key={category} sx={{ padding: "8px" }}>
              <Button
                className="button"
                onClick={() => {
                  setCategory({ category, processDefinitions });
                }}
                sx={{
                  borderLeftColor: theme.palette.primary.main,
                  borderLeftWidth: "4px",
                  borderLeftStyle: "solid",
                  boxShadow: 1,
                  borderRadius: "12px",
                  width: "100%",
                }}
                variant="contained"
              >
                {category === "Sem Categoria" ? t("home.others") : category}
              </Button>
            </AccordionDetails>
          ))}
        {dataCategories.length === 0 ? (
          <Box sx={{ textAlign: "center", padding: "16px" }}>
            <Typography color="text.secondary" variant="body1">
              {t("home.noCategory")}
            </Typography>
          </Box>
        ) : (
          <Pagination
            itemsPerPage={itemsPerPage}
            page={page}
            setPage={setPage}
            totalItems={categoriesProcess.length}
          />
        )}
      </Accordion>
    </Box>
  );
};

export default CategorySelection;
